import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { DashboardPayload, Language, AIProvider } from '../types';
import { loadLocale } from '../utils/translations';
import { setActiveEnterpriseId } from '../services/activeEnterprise';
import { currentEnterpriseIsReadOnly, type EnterpriseAccess } from '../utils/enterpriseGroup';

export interface EnterpriseOption {
  id: string;
  name: string;
  logo: string;
  /** 'group_read' = filiale héritée via l'admin de l'entreprise mère (lecture seule). */
  access?: EnterpriseAccess;
}

interface DashboardContextType {
  payload: DashboardPayload | null;
  setPayload: (payload: DashboardPayload | null) => void;
  language: Language;
  setLanguage: (lang: Language) => void;
  aiProvider: AIProvider;
  setAiProvider: (p: AIProvider) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  error: string | null;
  setError: (error: string | null) => void;
  enterpriseId: string;
  setEnterpriseId: (id: string) => void;
  enterprises: EnterpriseOption[];
  setEnterprises: (list: EnterpriseOption[]) => void;
  /** true quand l'entreprise sélectionnée est une filiale consultée en lecture seule. */
  enterpriseReadOnly: boolean;
}

const DashboardContext = createContext<DashboardContextType | undefined>(undefined);

const FALLBACK_ID = import.meta.env.REACT_APP_ENTERPRISE_ID || '';
const SUPPORTED_LANGS = ['en', 'fr', 'pt', 'de', 'nl', 'it', 'es', 'ka', 'ru', 'zh', 'ja', 'he', 'ar', 'sk', 'cs'];

function readLS<T>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    if (!v) return fallback;
    try {
      return JSON.parse(v) as T;
    } catch {
      return v as T;
    }
  } catch {
    return fallback;
  }
}

function detectLanguage(): Language {
  try {
    const saved = localStorage.getItem('tc-language');
    if (saved) return JSON.parse(saved) as Language;
    const browser = navigator.language?.slice(0, 2).toLowerCase();
    return (SUPPORTED_LANGS.includes(browser) ? browser : 'en') as Language;
  } catch { return 'en'; }
}

export const DashboardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [payload, setPayload] = useState<DashboardPayload | null>(null);
  const [language, setLanguageState] = useState<Language>(detectLanguage);
  const [aiProvider, setAiProviderState] = useState<AIProvider>('openai');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enterpriseId, setEnterpriseIdState] = useState<string>(() => {
    const id = readLS('tc-enterprise-id', FALLBACK_ID);
    setActiveEnterpriseId(id);   // lisible par les services dès le 1er appel
    return id;
  });
  const [enterprises, setEnterprises] = useState<EnterpriseOption[]>([]);
  const [, setLocaleVersion] = useState(0);

  // Load locale on startup and language change; bump version to trigger re-render
  useEffect(() => {
    loadLocale(language).then(() => setLocaleVersion(v => v + 1));
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    try { localStorage.setItem('tc-language', JSON.stringify(lang)); } catch {}
  };

  const setAiProvider = (p: AIProvider) => {
    setAiProviderState(p);
    try { localStorage.setItem('tc-ai-provider', JSON.stringify(p)); } catch {}
  };

  const setEnterpriseId = (id: string) => {
    setActiveEnterpriseId(id);
    setEnterpriseIdState(id);
    try { localStorage.setItem('tc-enterprise-id', id); } catch {}
  };

  const value = useMemo(() => ({
    payload, setPayload,
    language, setLanguage,
    aiProvider, setAiProvider,
    isLoading, setIsLoading,
    error, setError,
    enterpriseId, setEnterpriseId,
    enterprises, setEnterprises,
    enterpriseReadOnly: currentEnterpriseIsReadOnly(enterprises, enterpriseId),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [payload, language, aiProvider, isLoading, error, enterpriseId, enterprises]);

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) throw new Error('useDashboard must be used within a DashboardProvider');
  return context;
};
